import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonVariant } from '../design/Button';

interface AudioButtonProps {
  src: string;
  label: string;
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  disabled?: boolean;
}

/** Play/pause toggle for a single audio URL. */
const AudioButton: React.FC<AudioButtonProps> = ({
  src,
  label,
  variant = 'secondary',
  size = 'md',
  disabled = false,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    setPlaying(false);
    const audio = new Audio(src);
    audioRef.current = audio;
    const onEnded = (): void => setPlaying(false);
    const onError = (): void => {
      console.error('[school] Audio failed to load', src);
      setFailed(true);
      setPlaying(false);
    };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.pause();
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audioRef.current = null;
    };
  }, [src]);

  const toggle = async (): Promise<void> => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    try {
      audio.currentTime = 0;
      await audio.play();
      setPlaying(true);
    } catch (err) {
      console.error('[school] Audio play failed', err);
      setFailed(true);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      icon={playing ? 'pause' : 'play'}
      onClick={toggle}
      disabled={disabled || !src || failed}
    >
      {failed ? t('school_audio_unavailable') : label}
    </Button>
  );
};

export default AudioButton;
